import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import * as XLSX from 'xlsx';
import apiClient from '../../../pages/keycloak/axiosInstance';

const FlaggedModulesReview = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const projectId = localStorage.getItem('selectedProject');
        const supplierName = sessionStorage.getItem('selectedSupplierTab');

        const response = await apiClient.post(`get_flagged_module_review_table`, {
          projectId,
          supplierName,
        });

        // Access the 'data' array from the response
        const apiData = response.data.data || [];

        // Map API response to table data structure
        const mappedData = apiData.map((item, index) => ({
          srNo: index + 1,
          palletNo: item.palletNumber,
          vehicleNumber: item.vehicleNumber,
          shippedOn: item.dateOfShipping,
          receivedOn: item.receivedOn,
          shippedAmount: item.shippedQty,
          receivedAmount: item.receivedQty,
          acceptedAmount: item.acceptedQty,
          rejectedAmount: item.rejectedQty,
          shortAmount: item.shortQty,
          excessAmount: item.excessQty,
          evidence: item.photosZipUrl,
          remarks: item.remarks || 'N/A',
        }));

        setTableData(mappedData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter data based on date range and search query
  const filteredData = tableData.filter((row) => {
    const shippedDate = dayjs(row.shippedOn);
    const isInDateRange =
      (!startDate || shippedDate.isAfter(dayjs(startDate).subtract(1, 'day'))) &&
      (!endDate || shippedDate.isBefore(dayjs(endDate).add(1, 'day')));

    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = Object.values(row).some((value) =>
      value.toString().toLowerCase().includes(searchLower)
    );

    return isInDateRange && (searchQuery ? matchesSearch : true);
  });

  // Function to download table data as Excel
  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Flagged Modules');
    XLSX.writeFile(workbook, 'Flagged_Modules_Review.xlsx');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Header */}
      <h1 className="text-3xl flex justify-center font-bold text-gray-800 mb-6">Flagged Modules Review</h1>

      {/* Filters and Download Button */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
        {/* Search Bar */}
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search by any field"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            />
          </svg>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4 gap-4">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="flex justify-start gap-4 bg-white p-2 rounded-lg shadow-md">
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              renderInput={(params) => <TextField {...params} />}
              className="bg-white rounded-md"
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              renderInput={(params) => <TextField {...params} />}
              className="bg-white rounded-md"
            />
          </div>
        </LocalizationProvider>
        {/* Download Button */}
        <button
          onClick={downloadExcel}
          className="flex justify-end bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition duration-200"
          disabled={loading || filteredData.length === 0}
        >
          Download Excel
        </button>
      </div>

      {/* Loading and Error States */}
      {loading && <div className="text-center text-gray-600">Loading...</div>}
      {error && <div className="text-center text-red-600">{error}</div>}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-700">
            <tr>
              {[
                'Sr.No',
                'Pallet No',
                'Vehicle Number',
                'Shipped On',
                'Received On',
                'Shipped Amount',
                'Received Amount',
                'Accepted Amount',
                'Total Rejected Amount',
                'Short Amount',
                'Excess Amount',
                'Evidence',
                'Remarks',
              ].map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {!loading && !error && filteredData.length === 0 && (
              <tr>
                <td colSpan="13" className="px-6 py-4 text-center text-sm text-gray-500">
                  No data found
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              filteredData.map((row) => (
                <tr key={row.srNo} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.srNo}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.palletNo}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.vehicleNumber}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.shippedOn}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.receivedOn}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.shippedAmount}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.receivedAmount}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.acceptedAmount}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.rejectedAmount}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.shortAmount}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.excessAmount}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm">
                    {row.evidence ? (
                      <a
                        href={row.evidence}
                        target="_self"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-800 underline"
                      >
                        LINK
                      </a>
                    ) : (
                      <span className="text-gray-900">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">{row.remarks}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FlaggedModulesReview;